/**
 * SnowflakeForm — Snowflake warehouse connection form (RHF-based).
 *
 * Two authentication methods (ACT2-1031):
 * - **Lookout-generated key** (`managed_key`, the default): Lookout generates an RSA key pair and
 *   builds setup SQL that a Snowflake admin runs to create a key-pair-authenticated service user. The
 *   SQL regenerates automatically as the editable fields change. No secrets are sent from the
 *   browser — the backend injects the private key on save.
 * - **Your own private key** (`key_pair`): the customer pastes the private key of a service user they
 *   already set up, plus its passphrase when the key is encrypted. No setup SQL: the public half is
 *   already on their user. The key is stored encrypted on the connection, as before the form went
 *   managed-key only. Any format the driver can normalise is accepted (`src/lib/pem-key.ts`), so the
 *   form only rejects a pasted public key or certificate.
 *
 * Account and
 * database are read-only: they are the bucket identity, either typed in step 2 or taken
 * from the RudderStack destination. The compute warehouse is editable, and so is the optional
 * Lookout compute warehouse (ACT2-1031), which only Lookout dials. Field order:
 * Account details → Service Username → Role → Schema → Setup SQL → Public Key → Fingerprint.
 *
 * The "Account details" disclosure always starts **open**. It holds both warehouses: the compute
 * warehouse is required and nothing prefills it for a hand-typed bucket (AI-1370), and the optional
 * Lookout compute warehouse (ACT2-1031) is a choice the customer makes on this screen. Starting
 * collapsed whenever the compute warehouse was prefilled hid that choice. The customer can still
 * collapse it.
 *
 * ## Service user and role names (ACT2-1116)
 *
 * With a Lookout-generated key the form derives both names itself, once the key pair's fingerprint
 * arrives (`deriveSnowflakeServiceNames`, from the `nameSeed` the parent passes). It fills a field only
 * while the admin has not typed in it, and replaces the pre-ACT2-1116 defaults a prefill may still hold.
 * A name equal to the derived one is *generated* (`IF NOT EXISTS` in the SQL); any other is *typed*
 * (a plain `CREATE`, so a taken name stops at Snowflake's "already exists"). No SQL is shown before the
 * fingerprint, so a preview never carries a name without its suffix.
 *
 * The role is chosen in two modes: create a new role (the derived name, editable) or use an existing
 * one (typed). The mode lives in the form only and is reported to the parent (`onRoleModeChange`) for
 * the existing-role Test copy. Either way the role is required, so Lookout always connects with an
 * explicit role and a user's `DEFAULT_ROLE` never matters.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import type { SnowflakeConnectionConfig, SnowflakeConnectionInput } from '@server/types';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { type ControllerRenderProps, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { CopyButton } from '@/_shared/components/CopyButton';
import { Button, ButtonGroup } from '@/_shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/_shared/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/_shared/components/ui/collapsible';
import { ComboboxField } from '@/_shared/components/ui/combobox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/_shared/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';
import { Textarea } from '@/_shared/components/ui/textarea';
import { useSyntaxStyle } from '@/_shared/hooks/useSyntaxStyle';
import { codeBlockCustomStyle, SyntaxHighlighter } from '@/_shared/lib/syntax';

import {
  generateExistingRoleActivationSQL,
  generateSetupSQL,
  SNOWFLAKE_IDENTIFIER_MAX_LENGTH,
  snowflakeIdentifierError,
  snowflakeRoleError,
  type SnowflakeRoleMode,
  snowflakeSetupAdminRoles,
} from '../../../lib/snowflake-setup';
import { DownloadButton } from '../shared/DownloadButton';
import {
  type FormDraft,
  useDraftDefaults,
  useResetOnChange,
  useWarehouseFormHandle,
  type WarehouseFormHandle,
} from '../shared/formHandle';
import { FormSection } from '../shared/FormSection';
import { HiddenFieldErrors } from '../shared/HiddenFieldErrors';
import { OutputSection } from '../shared/OutputSection';
import {
  deriveSnowflakeServiceNames,
  isLegacySnowflakeDefault,
  joinClauses,
  type SnowflakeNameSeed,
  type SnowflakeServiceNames,
} from '../shared/utils';
import { LOOKOUT_WAREHOUSE_HINT, optionalWarehouseNameSchema } from './lookoutWarehouseField';
import { useSnowflakeKeypair } from './useSnowflakeKeypair';

interface SnowflakeFormProps {
  /** Values typed before the add flow remounted the form for a new account (see `useDraftDefaults`). */
  draft?: FormDraft;
  /** Where the warehouse suggestion popups portal — the drawer's sheet content, so they stay clickable. */
  portalContainer?: React.RefObject<HTMLElement | null>;
  /** The add flow names the account above the form (ACT2-1031), so its identity fields are not repeated here. */
  hideIdentity?: boolean;
  /** Prefilled config from the hook — account details + the Lookout-managed username/role defaults. */
  initialConfig?: SnowflakeConnectionConfig;
  /** Distinct compute-warehouse values seen across the bucket's destinations (dropdown suggestions). */
  availableWarehouses?: string[];
  /** Fired whenever a field changes, so the parent can reset a stale test result. */
  onChange?: () => void;
  /**
   * Offer only the customer's own private key. Set when Snowflake accounts are created on the
   * RudderStack platform: there the platform owns the generated key, and a Lookout-generated key
   * created here would bypass it.
   */
  ownKeyOnly?: boolean;
  /** Fired with whether "Your own private key" is selected, so the parent can swap its setup note. */
  onOwnKeyChange?: (ownKey: boolean) => void;
  /** The parent's setup note, shown after the warehouses and before the authentication method. */
  setupNote?: React.ReactNode;
  /**
   * ACT2-1116: what the generated service user and role names derive from, with the key fingerprint.
   * Null or absent: nothing is derived, and the names are whatever the prefill or the admin gives.
   */
  nameSeed?: SnowflakeNameSeed | null;
  /** Fired with the role mode and the role it names, so the parent can phrase a failed Test. */
  onRoleModeChange?: (mode: SnowflakeRoleMode, role: string) => void;
}

const COMPUTE_WAREHOUSE_HINT = 'Runs Lookout queries and reverse ETL syncs.';

/** Auth methods offered by the toggle. Maps to SnowflakeConnectionConfig['authType']. */
type SnowflakeFormAuthType = 'managed_key' | 'key_pair';

interface SnowflakeFormValues {
  authType: SnowflakeFormAuthType;
  privateKey: string;
  privateKeyPassphrase: string;
  username: string;
  /** The role Lookout creates (`new` mode), or the role of the customer's own key. */
  role: string;
  /** Lookout-generated key only (ACT2-1116). Not saved: it only shapes the setup SQL. */
  roleMode: SnowflakeRoleMode;
  /** `existing` mode: the customer's role, used as it is. Kept apart so a mode switch loses neither. */
  existingRole: string;
  schema: string;
  account: string;
  database: string;
  warehouse: string;
  /**
   * ACT2-1031: a separate warehouse for Lookout's own queries. Saved on the connection in Lookout only
   * (the server keeps it out of the credential and out of RudderStack); blank means Lookout uses the
   * compute warehouse. Also adds a grant to the setup SQL.
   */
  lookoutWarehouse: string;
}

/** A trimmed non-empty string, or undefined when blank — keeps empty fields out of the SQL. */
function blankToUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed;
}

/** A PEM block that is not a private key: a pasted public key or certificate, the usual mix-up. */
const NON_PRIVATE_PEM = /-----BEGIN (?![A-Z ]*PRIVATE KEY-----)[A-Z ]+-----/;

const SchemaModel = z
  .object({
    authType: z.enum(['managed_key', 'key_pair']),
    privateKey: z.string(),
    privateKeyPassphrase: z.string(),
    username: z.string().trim().min(1, 'Service username is required'),
    role: z.string(),
    roleMode: z.enum(['new', 'existing']),
    existingRole: z.string(),
    schema: z.string(),
    account: z.string().trim().min(1, 'Account missing — re-sync this destination from RudderStack'),
    database: z.string().trim().min(1, 'Database missing — re-sync this destination from RudderStack'),
    // Editable, and the one account-detail field a bucket never carries — so it is phrased as
    // something to enter, not as a destination to go re-sync.
    warehouse: z
      .string()
      .trim()
      .min(1, 'Compute warehouse is required')
      .max(SNOWFLAKE_IDENTIFIER_MAX_LENGTH, 'Warehouse name is too long'),
    lookoutWarehouse: optionalWarehouseNameSchema,
  })
  .superRefine((values, ctx) => {
    if (values.authType === 'managed_key') {
      // The setup SQL carries these names, so check them against its own rule. A name it cannot carry
      // is then reported on its field, instead of hiding the whole script behind "Complete the required
      // fields". Your own key has no setup SQL, so any name Snowflake accepts is fine there.
      const rolePath = values.roleMode === 'existing' ? 'existingRole' : 'role';
      if (!values[rolePath].trim()) ctx.addIssue({ code: 'custom', path: [rolePath], message: 'Role is required' });
      for (const path of ['username', rolePath, 'schema', 'warehouse', 'lookoutWarehouse'] as const) {
        const value = values[path].trim();
        // The service user gets the role in either mode, so an admin role is refused too.
        const message =
          value === ''
            ? null
            : path === rolePath
              ? snowflakeRoleError(value, values.roleMode)
              : snowflakeIdentifierError(value);
        if (message !== null) ctx.addIssue({ code: 'custom', path: [path], message });
      }
      return;
    }
    if (!values.privateKey.trim()) {
      ctx.addIssue({ code: 'custom', path: ['privateKey'], message: 'Private key is required' });
    } else if (NON_PRIVATE_PEM.test(values.privateKey)) {
      ctx.addIssue({
        code: 'custom',
        path: ['privateKey'],
        message: 'This is not a private key. Paste the private key, not the public key or a certificate.',
      });
    }
  }) satisfies z.ZodType<SnowflakeFormValues>;

export const SnowflakeForm = React.forwardRef<WarehouseFormHandle, SnowflakeFormProps>(function SnowflakeForm(
  {
    draft,
    initialConfig,
    availableWarehouses = [],
    onChange,
    ownKeyOnly = false,
    onOwnKeyChange,
    hideIdentity = false,
    portalContainer,
    setupNote,
    nameSeed = null,
    onRoleModeChange,
  },
  ref,
): React.JSX.Element {
  // A managed-key prefill carries Lookout's suggested service user and role. They name a user the
  // setup SQL will create, so they mean nothing for the customer's own key: that form starts with
  // both empty, and switching to it clears them (switching back restores them). Whatever was typed in
  // one mode is kept per mode, so switching away and back returns it rather than the default.
  const identityByAuthType = React.useRef<
    Partial<Record<SnowflakeFormValues['authType'], { username: string; role: string }>>
  >({});
  const prefillIdentity = {
    username: initialConfig?.authType === 'key_pair' ? '' : (initialConfig?.username ?? ''),
    role: initialConfig?.authType === 'key_pair' ? '' : (initialConfig?.role ?? ''),
  };
  const startsOwnKey = ownKeyOnly || initialConfig?.authType === 'key_pair';
  const { base, defaults } = useDraftDefaults<SnowflakeFormValues>(
    {
      authType: startsOwnKey ? 'key_pair' : 'managed_key',
      privateKey: '',
      privateKeyPassphrase: '',
      username: startsOwnKey && initialConfig?.authType !== 'key_pair' ? '' : (initialConfig?.username ?? ''),
      role: startsOwnKey && initialConfig?.authType !== 'key_pair' ? '' : (initialConfig?.role ?? ''),
      roleMode: 'new',
      existingRole: '',
      schema: initialConfig?.schema ?? '',
      account: initialConfig?.account ?? '',
      database: initialConfig?.database ?? '',
      warehouse: initialConfig?.warehouse ?? '',
      lookoutWarehouse: '',
    },
    draft,
  );
  const form = useForm<SnowflakeFormValues>({
    resolver: zodResolver(SchemaModel),
    mode: 'onTouched',
    defaultValues: defaults,
  });

  const warehouseTitleId = React.useId();
  const warehouseOptions = React.useMemo(
    () => availableWarehouses.map((wh) => ({ value: wh, label: wh })),
    [availableWarehouses],
  );
  // Type or pick: the destinations' warehouses are suggestions, never the only choice.
  const warehouseInput = (
    field: ControllerRenderProps<SnowflakeFormValues, 'warehouse' | 'lookoutWarehouse'>,
    label: string,
    placeholder: string,
  ): React.ReactNode =>
    warehouseOptions.length > 0 ? (
      <ComboboxField
        mode="single"
        allowCustomValue
        aria-label={label}
        options={warehouseOptions}
        value={field.value}
        onValueChange={field.onChange}
        onBlur={field.onBlur}
        placeholder={placeholder}
        size="lg"
        className="w-full"
        portalContainer={portalContainer}
      />
    ) : (
      <Input {...field} placeholder={placeholder} />
    );

  // Setup SQL regenerates automatically once the key pair lands and a username is
  // set. Transiently-invalid identifiers (mid-typing) just suppress the SQL. Watch
  // only the SQL-driving fields (not the whole form) so typing in unrelated fields
  // doesn't re-tokenize the highlighted SQL block.
  const [
    watchedAuthType,
    watchedUsername,
    watchedRole,
    watchedRoleMode,
    watchedExistingRole,
    watchedSchema,
    watchedWarehouse,
    watchedDatabase,
    watchedLookoutWarehouse,
  ] = useWatch({
    control: form.control,
    name: [
      'authType',
      'username',
      'role',
      'roleMode',
      'existingRole',
      'schema',
      'warehouse',
      'database',
      'lookoutWarehouse',
    ],
  });
  const isOwnKey = watchedAuthType === 'key_pair';
  React.useEffect(() => {
    onOwnKeyChange?.(isOwnKey);
  }, [isOwnKey, onOwnKeyChange]);

  // Own key: no setup SQL to build, so never fetch (or, on first use, create) the workspace key pair.
  const {
    keypairInfo,
    failed: keypairFailed,
    isFetching: keypairFetching,
    retry: retryKeypair,
  } = useSnowflakeKeypair(!isOwnKey);

  // ACT2-1116: derive the generated names once the fingerprint is known. The seed is compared by its
  // fields, not its identity, because the parent rebuilds it on every render.
  const seedSlug = nameSeed?.workspaceSlug ?? null;
  const seedAccountKey = nameSeed?.accountKey ?? null;
  const fingerprint = keypairInfo?.fingerprint ?? null;
  const [derived, setDerived] = React.useState<SnowflakeServiceNames | null>(null);
  React.useEffect(() => {
    setDerived(null);
    if (seedSlug === null || seedAccountKey === null || fingerprint === null) return;
    let cancelled = false;
    void deriveSnowflakeServiceNames({ workspaceSlug: seedSlug, accountKey: seedAccountKey }, fingerprint).then(
      (names) => {
        if (!cancelled) setDerived(names);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [seedSlug, seedAccountKey, fingerprint]);

  // Which names the admin typed. A field they typed in keeps its value; any other takes the derived
  // name. Separate from RHF's dirty state, which a programmatic setValue would also set. A non-empty
  // starting value counts as typed unless it is a pre-ACT2-1116 default from the prefill. A value the
  // draft carried over (it differs from the prefill) was typed, whatever it looks like: a typed
  // LOOKOUT_CUSTOM_SERVICE_USER matches the old default's pattern and must still survive a remount.
  const startsTyped = (field: 'username' | 'role'): boolean => {
    const value = defaults[field];
    if (value.trim() === '') return false;
    if (value !== base[field]) return true;
    return !isLegacySnowflakeDefault(field, value, seedSlug ?? '');
  };
  const typedName = React.useRef({ username: startsTyped('username'), role: startsTyped('role') });
  React.useEffect(() => {
    if (derived === null || form.getValues('authType') !== 'managed_key') return;
    for (const field of ['username', 'role'] as const) {
      if (!typedName.current[field]) form.setValue(field, derived[field], { shouldDirty: true });
    }
  }, [derived, form]);
  /** Mark a name as typed once the admin edits it; an emptied field is no longer typed. */
  const onNameInput =
    (field: 'username' | 'role', onFieldChange: (e: React.ChangeEvent<HTMLInputElement>) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      typedName.current[field] = e.target.value.trim() !== '';
      onFieldChange(e);
    };
  /** Leaving a name empty hands it back to the derived default, so clearing a typed name restores it. */
  const onNameBlur = (field: 'username' | 'role', onFieldBlur: () => void) => (): void => {
    // Managed key only: your own key's user is the customer's, never a derived name.
    if (derived !== null && form.getValues('authType') === 'managed_key' && !form.getValues(field).trim()) {
      form.setValue(field, derived[field], { shouldDirty: true, shouldValidate: true });
    }
    onFieldBlur();
  };

  const roleMode: SnowflakeRoleMode = watchedRoleMode;
  // As the SQL spells it: unquoted identifiers fold to upper case.
  const databaseLabel = watchedDatabase.trim().toUpperCase();
  const sqlRole = roleMode === 'existing' ? watchedExistingRole : watchedRole;
  const sameName = (value: string, generated: string | undefined): boolean =>
    generated !== undefined && value.trim().toUpperCase() === generated;
  const generatedUser = sameName(watchedUsername, derived?.username);
  const generatedRole = roleMode === 'new' && sameName(watchedRole, derived?.role);
  // Names are derived only when there is a seed; without one there is nothing to wait for.
  const namesPending = !isOwnKey && nameSeed !== null && derived === null;

  React.useEffect(() => {
    if (!isOwnKey) onRoleModeChange?.(roleMode, sqlRole.trim());
  }, [isOwnKey, roleMode, sqlRole, onRoleModeChange]);

  /** The setup SQL, or the generator's own message naming the field it could not carry. */
  const setup = React.useMemo((): { sql: string; activationSql: string | null } | { error: string } | null => {
    if (!keypairInfo || namesPending || !watchedUsername.trim() || !sqlRole.trim()) return null;
    if (!watchedWarehouse || !watchedDatabase) return null;
    try {
      const params = {
        username: watchedUsername.trim(),
        publicKeyBody: keypairInfo.publicKeyBody,
        publicKeyFingerprint: keypairInfo.fingerprint,
        warehouse: watchedWarehouse,
        lookoutWarehouse: blankToUndefined(watchedLookoutWarehouse),
        database: watchedDatabase,
        schema: blankToUndefined(watchedSchema),
        role: sqlRole,
        roleMode,
        generatedUser,
        generatedRole,
      };
      return {
        sql: generateSetupSQL(params),
        activationSql: roleMode === 'existing' ? generateExistingRoleActivationSQL(params) : null,
      };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'The setup SQL could not be generated.' };
    }
  }, [
    keypairInfo,
    namesPending,
    watchedUsername,
    sqlRole,
    roleMode,
    generatedUser,
    generatedRole,
    watchedSchema,
    watchedWarehouse,
    watchedDatabase,
    watchedLookoutWarehouse,
  ]);
  const setupSQL = setup !== null && 'sql' in setup ? setup.sql : null;
  const typedNames = !generatedUser || (roleMode === 'new' && !generatedRole);

  // What the managed-key setup SQL still needs before it can be generated. The key
  // pair is fetched separately and surfaced in its own Key Pair section.
  const setupMissing = React.useMemo(() => {
    const missing: string[] = [];
    if (!watchedUsername.trim()) missing.push('service username');
    if (!sqlRole.trim()) missing.push(roleMode === 'existing' ? 'your existing role' : 'role');
    // "compute warehouse", not "warehouse": on a page about warehouse accounts the bare word
    // reads as the account itself, which is exactly the field the customer already filled in.
    if (!watchedWarehouse) missing.push('compute warehouse');
    if (!watchedDatabase) missing.push('database');
    return missing;
  }, [watchedUsername, sqlRole, roleMode, watchedWarehouse, watchedDatabase]);

  const buildConfig = React.useCallback((values: SnowflakeFormValues): SnowflakeConnectionInput => {
    const config: SnowflakeConnectionInput = {
      account: values.account.trim(),
      username: values.username.trim(),
      database: values.database.trim(),
      warehouse: values.warehouse.trim(),
      authType: values.authType,
      // Always sent, so clearing the field clears the stored value. Lookout-only: the server stores
      // it beside the connection and never in the credential RudderStack receives.
      lookoutWarehouse: values.lookoutWarehouse.trim(),
    };
    if (values.schema.trim()) config.schema = values.schema.trim();
    const role =
      values.authType === 'managed_key' && values.roleMode === 'existing' ? values.existingRole : values.role;
    if (role.trim()) config.role = role.trim();
    // managed_key: the backend injects the private key — no secrets from the browser.
    if (values.authType === 'key_pair') {
      config.privateKey = values.privateKey.trim();
      if (values.privateKeyPassphrase) config.privateKeyPassphrase = values.privateKeyPassphrase;
    }
    return config;
  }, []);

  // The draft's defaults carry the derived names, so a remount (a new account) treats a derived name as
  // untouched and derives afresh, while a typed name rides across.
  const draftBase = React.useMemo(
    () => (derived === null ? base : { ...base, username: derived.username, role: derived.role }),
    [base, derived],
  );
  useWarehouseFormHandle(ref, form, buildConfig, draftBase);
  const syntaxStyle = useSyntaxStyle();
  // A passing test no longer reflects edited values — let the parent reset it.
  useResetOnChange(form, onChange);

  const warehouseBox = (
    // One card for both: the compute warehouse reverse ETL uses, and Lookout's optional override.
    <Card role="group" aria-labelledby={warehouseTitleId} className="gap-4 py-4">
      <CardHeader className="px-4">
        <CardTitle id={warehouseTitleId} className="text-sm font-medium">
          Warehouses
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 px-4">
        <FormField
          control={form.control}
          name="warehouse"
          render={({ field }) => (
            <FormSection label="Compute Warehouse" description={COMPUTE_WAREHOUSE_HINT}>
              {warehouseInput(field, 'Compute Warehouse', 'COMPUTE_WH')}
            </FormSection>
          )}
        />
        <FormField
          control={form.control}
          name="lookoutWarehouse"
          render={({ field }) => (
            <FormSection label="Lookout compute warehouse" optional description={LOOKOUT_WAREHOUSE_HINT}>
              {warehouseInput(field, 'Lookout compute warehouse', 'LOOKOUT_WH')}
            </FormSection>
          )}
        />
      </CardContent>
    </Card>
  );

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="grid gap-5" noValidate>
        {hideIdentity && (
          <HiddenFieldErrors
            control={form.control}
            fields={[
              { name: 'account', label: 'Account identifier' },
              { name: 'database', label: 'Database' },
            ]}
          />
        )}
        {/* On the add page the account and database live above the form, so the warehouses are all
            that is left of "Account details": show them directly rather than behind a disclosure. */}
        {hideIdentity ? (
          warehouseBox
        ) : (
          <Collapsible className="grid gap-4" defaultOpen>
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="text"
                className="group justify-self-start text-sm font-medium text-muted-foreground hover:text-foreground"
                suffixIcon={<ChevronDown className="transition-transform group-data-[state=open]:rotate-180" />}
              >
                Account details
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="grid gap-4">
              <FormField
                control={form.control}
                name="account"
                render={({ field }) => (
                  <FormSection label="Account">
                    <Input {...field} readOnly aria-readonly placeholder="xy12345.us-east-1" />
                  </FormSection>
                )}
              />
              <FormField
                control={form.control}
                name="database"
                render={({ field }) => (
                  <FormSection label="Database">
                    <Input {...field} readOnly aria-readonly placeholder="ANALYTICS" />
                  </FormSection>
                )}
              />
              {warehouseBox}
            </CollapsibleContent>
          </Collapsible>
        )}

        {setupNote}

        {!ownKeyOnly && (
          <FormField
            control={form.control}
            name="authType"
            render={({ field }) => (
              <FormItem className="rounded-xl border border-grey-50 bg-grey-25 p-4">
                <FormLabel>Authentication method</FormLabel>
                <FormDescription>How Lookout authenticates to your Snowflake service user.</FormDescription>
                <Select
                  value={field.value}
                  onValueChange={(next) => {
                    const nextAuthType = next as SnowflakeFormValues['authType'];
                    identityByAuthType.current[field.value] = {
                      username: form.getValues('username'),
                      role: form.getValues('role'),
                    };
                    field.onChange(nextAuthType);
                    const generatedIdentity = derived ?? prefillIdentity;
                    const restored =
                      identityByAuthType.current[nextAuthType] ??
                      (nextAuthType === 'key_pair' ? { username: '', role: '' } : generatedIdentity);
                    form.setValue('username', restored.username, { shouldDirty: true });
                    form.setValue('role', restored.role, { shouldDirty: true });
                    // A restored value is typed unless it is the derived default.
                    typedName.current = {
                      username: restored.username !== '' && restored.username !== derived?.username,
                      role: restored.role !== '' && restored.role !== derived?.role,
                    };
                  }}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="managed_key">Lookout-generated key</SelectItem>
                    <SelectItem value="key_pair">Your own private key</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="username"
          render={({ field }) => (
            <FormSection label="Service Username" description="The user Lookout signs in as.">
              <Input
                {...field}
                onChange={onNameInput('username', field.onChange)}
                onBlur={onNameBlur('username', field.onBlur)}
                placeholder={derived?.username ?? 'LOOKOUT_SERVICE_USER'}
              />
            </FormSection>
          )}
        />

        {isOwnKey ? (
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormSection label="Role" optional>
                <Input {...field} placeholder="LOOKOUT_ROLE" />
              </FormSection>
            )}
          />
        ) : (
          <>
            <FormField
              control={form.control}
              name="roleMode"
              render={({ field }) => (
                <FormItem className="rounded-xl border border-grey-50 bg-grey-25 p-4">
                  <FormLabel className="px-1">Which role Lookout uses</FormLabel>
                  <FormControl>
                    <RadioGroup value={field.value} onValueChange={field.onChange}>
                      <RadioGroupItem value="new">Create a new role for Lookout (recommended)</RadioGroupItem>
                      <RadioGroupItem value="existing">Use an existing role</RadioGroupItem>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
            {roleMode === 'new' ? (
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormSection
                    label="Role"
                    description={`Lookout creates this role and grants it read access to ${databaseLabel || 'your database'}.`}
                  >
                    <Input
                      {...field}
                      onChange={onNameInput('role', field.onChange)}
                      onBlur={onNameBlur('role', field.onBlur)}
                      placeholder={derived?.role ?? 'LOOKOUT_ROLE'}
                    />
                  </FormSection>
                )}
              />
            ) : (
              <FormField
                control={form.control}
                name="existingRole"
                render={({ field }) => (
                  <FormSection
                    label="Existing role"
                    description={`The role must already exist. The setup SQL grants it USAGE on the warehouse and ${databaseLabel || 'your database'} (so everyone who holds it can spend that warehouse's credits) and changes nothing else, so make sure it can read ${databaseLabel || 'your database'}. Activation also needs USAGE + CREATE TABLE on ${databaseLabel || '<database>'}._RUDDERSTACK — the optional SQL below grants them. Lookout acts with every privilege this role has; pick a read-only role.`}
                  >
                    <Input {...field} placeholder="ANALYST" />
                  </FormSection>
                )}
              />
            )}
          </>
        )}

        <FormField
          control={form.control}
          name="schema"
          render={({ field }) => (
            <FormSection label="Schema" optional>
              <Input {...field} placeholder="PUBLIC" />
            </FormSection>
          )}
        />

        {isOwnKey ? (
          <>
            <FormField
              control={form.control}
              name="privateKey"
              render={({ field }) => (
                <FormSection
                  label="Private Key"
                  description="Its public key must be set on this user. Stored encrypted."
                >
                  <Textarea
                    {...field}
                    rows={6}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={'-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'}
                    className="max-h-60 overflow-auto font-mono"
                  />
                </FormSection>
              )}
            />
            <FormField
              control={form.control}
              name="privateKeyPassphrase"
              render={({ field }) => (
                <FormSection label="Private Key Passphrase" description="Only if the key is encrypted." optional>
                  <Input {...field} type="password" autoComplete="off" />
                </FormSection>
              )}
            />
          </>
        ) : setupSQL !== null && setup !== null && 'sql' in setup ? (
          <>
            <OutputSection
              label="Setup SQL"
              description={
                <>
                  Run in a Snowflake worksheet as a user who holds {snowflakeSetupAdminRoles(roleMode)}.
                  {typedNames && (
                    <>
                      {' '}
                      If Snowflake says a name &lsquo;already exists&rsquo;, choose another name in Lookout and copy the
                      SQL again. If you re-run after a failure, a name from this same script that already exists will
                      stop it with &lsquo;already exists&rsquo;. Drop it or type a new name.
                    </>
                  )}
                </>
              }
              action={
                <ButtonGroup>
                  <DownloadButton text={setupSQL} filename="rudder-lookout-setup.sql" />
                  <CopyButton text={setupSQL} label="Copy" copyLabel="Setup SQL" />
                </ButtonGroup>
              }
            >
              <SyntaxHighlighter language="sql" style={syntaxStyle} customStyle={codeBlockCustomStyle}>
                {setupSQL}
              </SyntaxHighlighter>
            </OutputSection>
            {setup.activationSql !== null && (
              <OutputSection
                label="Optional: grants for activation"
                description="Lookout never runs these. Every line is commented out: uncomment and run only the grants your role lacks."
                action={<CopyButton text={setup.activationSql} label="Copy" copyLabel="Activation grants" />}
              >
                <SyntaxHighlighter language="sql" style={syntaxStyle} customStyle={codeBlockCustomStyle}>
                  {setup.activationSql}
                </SyntaxHighlighter>
              </OutputSection>
            )}
          </>
        ) : setup !== null && 'error' in setup ? (
          <OutputSection label="Setup SQL" error>
            {setup.error}
          </OutputSection>
        ) : keypairInfo && !namesPending ? (
          <OutputSection label="Setup SQL" error>
            {setupMissing.length
              ? `Enter ${joinClauses(setupMissing)} to generate the setup SQL.`
              : 'Complete the required fields to generate the setup SQL.'}
          </OutputSection>
        ) : keypairFailed ? null : (
          <OutputSection label="Setup SQL">
            <p role="status" className="text-xs text-muted-foreground">
              Generating the key pair…
            </p>
          </OutputSection>
        )}

        {isOwnKey ? null : keypairInfo ? (
          <>
            <OutputSection
              label="Public Key"
              action={<CopyButton text={keypairInfo.publicKeyPem} label="Copy" copyLabel="Public Key" />}
            >
              <pre className="font-mono text-xs whitespace-pre text-grey-800">{keypairInfo.publicKeyPem}</pre>
            </OutputSection>
            <OutputSection
              label="Fingerprint"
              action={<CopyButton text={keypairInfo.fingerprint} label="Copy" copyLabel="Fingerprint" />}
            >
              <code className="block font-mono text-xs break-all text-grey-800">{keypairInfo.fingerprint}</code>
            </OutputSection>
          </>
        ) : keypairFailed ? (
          <OutputSection
            label="Key Pair"
            error
            action={
              <Button type="button" variant="secondary" size="sm" loading={keypairFetching} onClick={retryKeypair}>
                Retry
              </Button>
            }
          >
            Lookout couldn't generate the workspace RSA key pair. Retry to continue.
          </OutputSection>
        ) : null}
      </form>
    </Form>
  );
});
